import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getErrorMessage } from '../../lib/errors';
import type { BusinessRead, ProfileRead } from '../../types/api';
import { useAuth } from '../auth/useAuth';
import { fetchBusinesses, fetchProfile } from './accountApi';
import { brandFromLocation } from './brandParam';
import { AccountContext, type AccountContextValue, type OnboardingStatus } from './accountContext';

interface AccountProviderProps {
  children: ReactNode;
}

interface LoadedAccount {
  userId: string;
  profile: ProfileRead | null;
  businesses: BusinessRead[];
  error: string | null;
}

/**
 * Which brand this user last worked in, on this device. Per user, so two
 * accounts on one machine never inherit each other's choice. Best effort:
 * storage can be disabled, and the fallback is the oldest brand.
 */
function activeBrandKey(userId: string): string {
  return `outpost:active-brand:${userId}`;
}

function readActiveBrand(userId: string): string | null {
  try {
    return window.localStorage.getItem(activeBrandKey(userId));
  } catch {
    return null;
  }
}

function writeActiveBrand(userId: string, businessId: string): void {
  try {
    window.localStorage.setItem(activeBrandKey(userId), businessId);
  } catch {
    // Storage disabled or full. The choice lasts for the session only.
  }
}

/** The remembered brand when it is still in the list, else the oldest one. */
function resolveActive(
  businesses: readonly BusinessRead[],
  remembered: string | null,
): BusinessRead | null {
  return businesses.find((entry) => entry.id === remembered) ?? businesses[0] ?? null;
}

/**
 * Loads the profile and the user's brands once per session and shares them
 * app-wide, together with which brand is active.
 *
 * Onboarding gating, the knowledge base (`business_id`) and the dashboard all
 * read from here, so a signed-in user pays for one profile fetch and one list
 * fetch, not one per screen. Every business-scoped hook keys its state on the
 * active `business_id`, so switching brands re-fetches without a reload.
 */
export function AccountProvider({ children }: AccountProviderProps) {
  const { user, isLoading: isAuthLoading } = useAuth();
  const [loaded, setLoaded] = useState<LoadedAccount | null>(null);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const userId = user?.id ?? null;
  const refresh = useCallback(() => setNonce((value) => value + 1), []);

  useEffect(() => {
    // No synchronous setState in this effect body: state for a signed-out or
    // switched user is filtered out during render by the `isCurrent` check
    // below, so there is nothing to clear here.
    if (isAuthLoading || !userId) return;

    const controller = new AbortController();
    let cancelled = false;

    void (async () => {
      try {
        const profile = await fetchProfile(controller.signal);
        // The list is empty, not an error, before onboarding; it is also what
        // says "onboarded" for an account whose profile flag never flipped.
        const businesses = await fetchBusinesses(controller.signal);

        if (cancelled) return;
        setLoaded({ userId, profile, businesses, error: null });
        // A link's brand wins over the remembered one when the user is in it.
        const linked = brandFromLocation();
        const fromLink = businesses.find((entry) => entry.id === linked);
        if (fromLink) writeActiveBrand(userId, fromLink.id);
        setActiveId(resolveActive(businesses, fromLink?.id ?? readActiveBrand(userId))?.id ?? null);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        // Fail open: a failed lookup must never lock a signed-in user out.
        console.warn('Account lookup failed; allowing through.', getErrorMessage(caught));
        setLoaded({
          userId,
          profile: null,
          businesses: [],
          error: getErrorMessage(caught, 'Could not load your account details.'),
        });
        setActiveId(null);
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [userId, isAuthLoading, nonce]);

  const setProfile = useCallback(
    (profile: ProfileRead) => {
      // Guarded on userId so a write cannot land on another user's cached record.
      setLoaded((current) =>
        current === null || current.userId !== userId
          ? current
          : { ...current, profile, error: null },
      );
    },
    [userId],
  );

  const setActiveBusinessId = useCallback(
    (businessId: string) => {
      if (!userId) return;
      setLoaded((current) => {
        if (current === null || current.userId !== userId) return current;
        if (!current.businesses.some((entry) => entry.id === businessId)) return current;
        writeActiveBrand(userId, businessId);
        setActiveId(businessId);
        return current;
      });
    },
    [userId],
  );

  const addBusiness = useCallback(
    (business: BusinessRead) => {
      if (!userId) return;
      setLoaded((current) => {
        if (current === null || current.userId !== userId) return current;
        const businesses = current.businesses.some((entry) => entry.id === business.id)
          ? current.businesses.map((entry) => (entry.id === business.id ? business : entry))
          : [...current.businesses, business];
        return { ...current, businesses, error: null };
      });
      writeActiveBrand(userId, business.id);
      setActiveId(business.id);
    },
    [userId],
  );

  const setBusiness = useCallback(
    (business: BusinessRead) => {
      setLoaded((current) =>
        current === null || current.userId !== userId
          ? current
          : {
              ...current,
              businesses: current.businesses.map((entry) =>
                entry.id === business.id ? business : entry,
              ),
            },
      );
    },
    [userId],
  );

  const removeBusiness = useCallback(
    (businessId: string) => {
      if (loaded === null || loaded.userId !== userId) return;
      const remaining = loaded.businesses.filter((entry) => entry.id !== businessId);
      setLoaded({ ...loaded, businesses: remaining });
      setActiveId((current) => (current === businessId ? null : current));
      // The server cleared onboarding_completed with the last brand; the gate
      // reads the profile, so it has to be fetched again.
      if (remaining.length === 0) refresh();
    },
    [loaded, userId, refresh],
  );

  const value = useMemo<AccountContextValue>(() => {
    const isCurrent = loaded !== null && loaded.userId === userId;
    const businesses = isCurrent ? loaded.businesses : [];
    const business = isCurrent ? resolveActive(businesses, activeId) : null;

    let onboardingStatus: OnboardingStatus;
    let isLoading = false;

    if (isAuthLoading) {
      onboardingStatus = 'loading';
      isLoading = true;
    } else if (!userId) {
      onboardingStatus = 'incomplete';
    } else if (!isCurrent) {
      onboardingStatus = 'loading';
      isLoading = true;
    } else if (loaded.profile === null) {
      onboardingStatus = 'unknown';
    } else {
      // A brand on file is onboarding, whatever the profile flag says: the flag
      // is the first-brand double-submit guard, the list is the truth.
      onboardingStatus =
        loaded.profile.onboarding_completed || businesses.length > 0 ? 'complete' : 'incomplete';
    }

    return {
      profile: isCurrent ? loaded.profile : null,
      business,
      businesses,
      onboardingStatus,
      isLoading,
      error: isCurrent ? loaded.error : null,
      refresh,
      setProfile,
      setActiveBusinessId,
      addBusiness,
      setBusiness,
      removeBusiness,
    };
  }, [
    loaded,
    userId,
    activeId,
    isAuthLoading,
    refresh,
    setProfile,
    setActiveBusinessId,
    addBusiness,
    setBusiness,
    removeBusiness,
  ]);

  return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}
